import { Injectable, inject, signal, Injector } from '@angular/core';
import { supabase } from '../supabase';
import { ContextService } from './context.service';
import { CompressionUtil } from '../utils/compression.util';
import {
    SystemLog, TipoDocumento, TipoAcao, ProtocoloFiscal, HistoricoAuditoria
} from '../models';

@Injectable({
    providedIn: 'root'
})
export class AuditService {
    private injector = inject(Injector);
    private get context(): ContextService {
        return this.injector.get(ContextService);
    }

    // State for logs (mirrored from legacy DB service if needed, or managed here)
    systemLogsTable = signal<SystemLog[]>([]);

    /**
     * Helper para descompactar dados (suporta tanto dados novos compactados quanto antigos em JSON)
     */
    private parseJsonField(field: any): any {
        if (!field) return null;
        if (typeof field === 'object') return field; // já é objeto (PostgreSQL jsonb)
        if (typeof field === 'string') {
            return CompressionUtil.decompress(field);
        }
        return null;
    }

    /**
     * Enriquece um log do sistema com dados adicionais do banco de dados (nomes de pacientes/procedimentos).
     */
    async enrichSystemLogAsync(log: SystemLog): Promise<SystemLog> {
        if (!log.details) log.details = {};

        const entityId = log.details.entidadeId || log.details.appointmentId || log.details.id || log.details.changes?.id;
        const entityTipo = log.details.entidadeTipo || (log.target?.includes('Agendamento') ? 'AGENDAMENTO' : log.target?.includes('Paciente') ? 'PACIENTE' : '');

        try {
            if (entityTipo === 'AGENDAMENTO' || log.target?.includes('Agendamento')) {
                const id = entityId || (log.target ? parseInt(log.target.split('#')[1]) : undefined);
                if (id) {
                    const { data: app } = await supabase
                        .from('appointments')
                        .select('*, patient:patients(id, name, social_name, cpf, sus_card), procedure:procedures(id, name, category)')
                        .eq('id', id)
                        .maybeSingle();

                    if (app) {
                        const pName = app.patient?.social_name || app.patient?.name;
                        const prName = app.procedure?.name;

                        log.details.patientName = log.details.patientName || pName;
                        log.details.name = log.details.name || pName;
                        log.details.procedureName = log.details.procedureName || prName;
                        log.details.procedure = log.details.procedure || prName;
                        // Status do agendamento real prevalece sobre 'ATIVO' do protocolo fiscal
                        log.details.status = (app.status && app.status !== 'ATIVO') ? app.status : (log.details.status || 'Aguardando');
                        log.details.priority = log.details.priority || app.priority || 'Normal';
                        log.details.patientId = log.details.patientId || app.patient_id;
                        log.details.patientCpf = log.details.patientCpf || app.patient?.cpf;
                        log.details.appointment = app;
                    }
                }
            } else if (entityTipo === 'PACIENTE' || log.target?.includes('Paciente')) {
                const id = entityId || (log.target ? parseInt(log.target.split('#')[1]) || parseInt(log.target.split('Paciente ')[1]) : undefined);
                if (id) {
                    const { data: p } = await supabase.from('patients').select('id, name, social_name, cpf, sus_card').eq('id', id).maybeSingle();
                    if (p) {
                        const name = p.social_name || p.name;
                        log.details.patientName = log.details.patientName || name;
                        log.details.name = log.details.name || name;
                        log.details.patientId = log.details.patientId || p.id;
                        log.details.patientCpf = log.details.patientCpf || p.cpf;
                    }
                }
            } else if (entityTipo === 'PROCEDIMENTO' || log.target?.includes('Procedimento')) {
                const id = entityId || (log.target ? parseInt(log.target.split('#')[1]) : undefined);
                if (id) {
                    const { data: pr } = await supabase.from('procedures').select('id, name, category').eq('id', id).maybeSingle();
                    if (pr) {
                        log.details.procedureName = log.details.procedureName || pr.name;
                        log.details.procedure = log.details.procedure || pr.name;
                    }
                }
            }
        } catch (e) {
            console.warn('[AUDIT] Erro no enriquecimento:', e);
        }
        return log;
    }

    /**
     * Busca um log por protocolo em Supabase (JSONB trails ou system_logs).
     */
    async findLogByProtocol(protocol: string) {
        const pTrim = protocol.trim();

        // 1. Buscar na tabela system_logs
        const { data: sysLog } = await supabase
            .from('system_logs')
            .select('*')
            .eq('protocol', pTrim)
            .maybeSingle();

        if (sysLog) {
            return {
                id: sysLog.id,
                protocol: sysLog.protocol,
                timestamp: new Date(sysLog.timestamp),
                action: sysLog.action,
                target: sysLog.target,
                user: sysLog.user_identifier || 'Sistema',
                municipalityId: sysLog.municipality_id,
                details: sysLog.details_json || {}
            } as SystemLog;
        }

        // 2. Buscar em audit_trail de agendamentos
        const { data: app } = await supabase
            .from('appointments')
            .select('id, procedure_id, municipality_id, audit_trail')
            .contains('audit_trail', [{ protocol: pTrim }])
            .maybeSingle();

        if (app && app.audit_trail) {
            const found = (app.audit_trail as any[]).find(l => l.protocol === pTrim);
            if (found) {
                return {
                    id: found.protocol,
                    protocol: found.protocol,
                    timestamp: new Date(found.date.split('/').reverse().join('-') + 'T' + found.time),
                    action: found.action,
                    target: `Agendamento #${app.id} `,
                    user: found.agent,
                    municipalityId: app.municipality_id,
                    details: found.details
                } as SystemLog;
            }
        }

        // 3. Buscar em audit_trail de pacientes
        const { data: patient } = await supabase
            .from('patients')
            .select('id, name, municipality_id, audit_trail')
            .contains('audit_trail', [{ protocol: pTrim }])
            .maybeSingle();

        if (patient && patient.audit_trail) {
            const found = (patient.audit_trail as any[]).find(l => l.protocol === pTrim);
            if (found) {
                return {
                    id: found.protocol,
                    protocol: found.protocol,
                    timestamp: new Date(found.date.split('/').reverse().join('-') + 'T' + found.time),
                    action: found.action,
                    target: `Paciente ${patient.name} `,
                    user: found.agent,
                    municipalityId: patient.municipality_id,
                    details: found.details
                } as SystemLog;
            }
        }

        return null;
    }

    /**
     * Busca um protocolo fiscal de forma assíncrona no banco de dados.
     */
    async findFiscalProtocolAsync(protocol: string, municipalityId?: string | null): Promise<SystemLog | null> {
        const pTrim = protocol.trim();
        let log: SystemLog | null = null;
        const normalizedMunId = (municipalityId && municipalityId !== 'SISTEMA' && municipalityId !== 'GLOBAL')
            ? String(municipalityId).trim().toUpperCase()
            : null;

        try {
            // 1. Tentar buscar na tabela de protocolos fiscais
            let protQuery = supabase
                .from('protocolos_fiscais')
                .select('*')
                .ilike('numero_protocolo', pTrim);

            if (normalizedMunId) {
                protQuery = protQuery.or(`municipio_id.ilike.${normalizedMunId},municipio_id.eq.SISTEMA`);
            }

            const { data: prot, error } = await protQuery.maybeSingle();

            if (error) throw error;

            if (prot) {
                const protMun = (prot.municipio_id || '').trim().toUpperCase();
                if (normalizedMunId && protMun && protMun !== 'SISTEMA' && protMun !== normalizedMunId) {
                    return null; // Pertence a outro município
                }

                log = {
                    id: prot.id?.toString() || crypto.randomUUID(),
                    protocol: prot.numero_protocolo || pTrim,
                    timestamp: new Date(prot.data_emissao || new Date()),
                    action: prot.tipo_documento || 'ACAO_SISTEMA',
                    target: prot.entidade_tipo === 'AGENDAMENTO' ? `Agendamento #${prot.entidade_id} ` : `Contexto: ${prot.entidade_tipo || 'Entidade'} #${prot.entidade_id || 'N/A'} `,
                    user: prot.operador_nome || prot.operador_login || 'Sistema (Automação)',
                    municipalityId: prot.municipio_id || 'SISTEMA',
                    details: {
                        tipoDocumento: prot.tipo_documento,
                        entidadeTipo: prot.entidade_tipo,
                        entidadeId: prot.entidade_id,
                        status: prot.status,
                        observacoes: prot.observacoes
                    }
                } as SystemLog;

                // Enriquecer com agendamento do banco se for entidade AGENDAMENTO
                if (prot.entidade_tipo === 'AGENDAMENTO' && prot.entidade_id) {
                    try {
                        let appQuery = supabase
                            .from('appointments')
                            .select('*, patient:patients(name, social_name, cpf, sus_card, phone, birth_date, address_street, address_number, address_district, address_city, address_uf), procedure:procedures(name, category)')
                            .eq('id', prot.entidade_id);

                        if (normalizedMunId) {
                            appQuery = appQuery.ilike('municipio_id', normalizedMunId);
                        }

                        const { data: appData } = await appQuery.maybeSingle();

                        if (appData) {
                            if (appData.municipality_id) {
                                log.municipalityId = appData.municipality_id;
                            }
                            const pName = appData.patient?.social_name || appData.patient?.name;
                            const prName = appData.procedure?.name;
                            log.details = {
                                ...log.details,
                                name: pName,
                                patientName: pName,
                                procedure: prName,
                                procedureName: prName,
                                procedureCategory: appData.procedure?.category,
                                status: appData.status || log.details.status,
                                priority: appData.priority || log.details.priority,
                                location: appData.schedule_location || appData.outcome_location || log.details.location,
                                schedulingData: appData.schedule_date ? { date: appData.schedule_date, time: appData.schedule_time || '' } : null,
                                appointment: appData
                            };
                        } else if (normalizedMunId && protMun === 'SISTEMA') {
                            // Protocolo SISTEMA pertencente a agendamento de outro município
                            return null;
                        }
                    } catch (e) {
                        console.warn('[AuditService] Erro ao enriquecer protocolo com agendamento:', e);
                    }
                }

                // Enriquecer com histórico
                let histQuery = supabase
                    .from('historico_auditoria')
                    .select('dados_novos, descricao_acao, operador_nome, municipio_id')
                    .eq('protocolo_referencia', pTrim)
                    .order('data_hora', { ascending: true })
                    .limit(1);

                if (normalizedMunId) {
                    histQuery = histQuery.ilike('municipio_id', normalizedMunId);
                }

                const { data: history } = await histQuery.maybeSingle();

                if (history) {
                    try {
                        const fullDetails = this.parseJsonField(history.dados_novos);
                        if (fullDetails) {
                            log.details = { ...log.details, ...fullDetails };
                        }
                        if (history.descricao_acao) log.details.event = history.descricao_acao;
                        if (history.operador_nome && !history.operador_nome.toLowerCase().includes('sistema')) {
                            log.user = history.operador_nome;
                        }
                    } catch (e) { }
                }
            } else {
                // Fallback para logic do agendamento
                let appFallbackQuery = supabase
                    .from('appointments')
                    .select('*, patient:patients(name, social_name), procedure:procedures(name, category)')
                    .or(`sub_protocol.eq.${pTrim}, latest_protocol.eq.${pTrim}`);

                if (normalizedMunId) {
                    appFallbackQuery = appFallbackQuery.ilike('municipio_id', normalizedMunId);
                }

                const { data: appData } = await appFallbackQuery.maybeSingle();

                if (appData) {
                    const effectiveProtocol = appData.sub_protocol || appData.latest_protocol;
                    const pName = appData.patient?.social_name || appData.patient?.name;
                    const prName = appData.procedure?.name;

                    log = {
                        id: appData.id.toString(),
                        protocol: effectiveProtocol,
                        timestamp: new Date(appData.registration_date || appData.created_at || new Date()),
                        action: appData.sub_protocol ? 'CIRURGIA_ETAPA' : 'AGENDAMENTO',
                        target: `Agendamento #${appData.id} `,
                        user: 'Sistema',
                        municipalityId: appData.municipality_id,
                        details: {
                            name: pName,
                            patientName: pName,
                            procedure: prName,
                            procedureName: prName,
                            procedureCategory: appData.procedure?.category,
                            status: appData.status,
                            priority: appData.priority,
                            subProtocol: appData.sub_protocol,
                            entidadeId: appData.id,
                            entidadeTipo: 'AGENDAMENTO',
                            appointment: { ...appData }
                        }
                    } as SystemLog;
                } else {
                    // Fallback final para trails genéricos
                    const foundLog = await this.findLogByProtocol(pTrim);
                    if (foundLog) {
                        const foundMun = (foundLog.municipalityId || '').trim().toUpperCase();
                        if (!normalizedMunId || foundMun === normalizedMunId) {
                            log = foundLog;
                        }
                    }
                }
            }
        } catch (err) {
            console.error('[AUDIT] Erro ao buscar protocolo fiscal:', err);
        }

        if (log && log.target) {
            await this.enrichSystemLogAsync(log);
        }

        return log;
    }

    /**
     * Generates a unique protocol and logs the action to the audit trail in Supabase.
     */
    async logAction(action: string, target: string, details: Record<string, any>, user?: string, preDefinedProtocol?: string) {
        const timestamp = new Date();
        const protocol = preDefinedProtocol || `LOG-${timestamp.getTime()}-${Math.floor(1000 + Math.random() * 9000)}`;

        const fallbackUser = this.context.currentOperator()?.name || 'Sistema';
        const effectiveUser = user || fallbackUser;

        const newLog: SystemLog = {
            id: crypto.randomUUID(),
            protocol,
            timestamp,
            action,
            target,
            user: effectiveUser,
            municipalityId: this.context.currentMunicipalityId() || 'NÃO IDENTIFICADO',
            details
        };

        const isGlobalEvent = !action.includes('PATIENT') && !action.includes('APPOINTMENT') && !action.includes('PROCEDURE') && !action.includes('STATUS');

        if (isGlobalEvent) {
            const { error } = await supabase.from('system_logs').insert({
                protocol,
                timestamp: timestamp.toISOString(),
                action,
                target,
                user_identifier: effectiveUser,
                municipality_id: this.context.currentMunicipalityId() || 'SISTEMA',
                details_json: details
            });

            if (error) {
                console.error('[AuditService] Erro ao gravar log global no Supabase:', error);
            }
        }

        // Update Local State
        this.systemLogsTable.update(logs => [newLog, ...logs]);

        return protocol;
    }

    async recordExportLog(type: string, target: string, protocol: string) {
        const op = this.context.currentOperator();
        const userName = op?.name || 'OPERADOR';
        return this.logAction(type, target, { protocol, exportDate: new Date() }, userName, protocol);
    }

    /**
     * Obtém o próximo número sequencial para o protocolo fiscal do dia.
     * Formato: PROT-YYYY-MMDD-XXXXX
     */
    private async obterProximoSequencial(dataFormatada: string): Promise<number> {
        try {
            const { data, error } = await supabase
                .from('protocolos_fiscais')
                .select('numero_protocolo')
                .like('numero_protocolo', `PROT-%-${dataFormatada}-%`)
                .order('numero_protocolo', { ascending: false })
                .limit(20);

            if (error) throw error;
            if (!data || data.length === 0) return 1;

            let maxSeq = 0;
            for (const item of data) {
                if (!item.numero_protocolo) continue;
                const parts = item.numero_protocolo.split('-');
                if (parts.length >= 4) {
                    const rawSeq = parts[3].split('.')[0];
                    const num = parseInt(rawSeq, 10);
                    if (!isNaN(num) && num > maxSeq) {
                        maxSeq = num;
                    }
                }
            }

            return maxSeq > 0 ? maxSeq + 1 : 1;
        } catch (err) {
            console.error('[AuditService] Erro ao obter sequencial:', err);
            return Math.floor(Math.random() * 9000) + 1000;
        }
    }

    /**
     * Gera um novo protocolo fiscal atômico e resiliente.
     */
    async gerarProtocolo(
        tipoDocumento: TipoDocumento,
        entidadeTipo: 'PACIENTE' | 'PROCEDIMENTO' | 'AGENDAMENTO',
        entidadeId: number,
        operadorId: number,
        operadorNome?: string,
        operadorLogin?: string,
        observacoes?: string,
        municipioId?: string | null
    ): Promise<string> {
        const opNome = operadorNome || this.context.currentOperator()?.name || 'Sistema';
        const opLogin = operadorLogin || this.context.currentOperator()?.login || 'system';
        const munId = municipioId || this.context.currentMunicipalityId() || 'SISTEMA';

        // 1. Tentar gerar de forma atômica no PostgreSQL via RPC (SECURITY DEFINER, sem bloqueio de RLS)
        try {
            const { data: rpcProtocol, error: rpcError } = await supabase.rpc('gerar_protocolo_fiscal', {
                p_tipo_documento: tipoDocumento,
                p_entidade_tipo: entidadeTipo,
                p_entidade_id: Number(entidadeId),
                p_operador_id: String(operadorId || '1'),
                p_operador_nome: opNome,
                p_operador_login: opLogin,
                p_municipio_id: munId,
                p_observacoes: observacoes || null
            });

            if (!rpcError && rpcProtocol && typeof rpcProtocol === 'string') {
                return rpcProtocol;
            }
            if (rpcError) {
                console.warn('[AuditService] RPC gerar_protocolo_fiscal falhou ou não existe ainda, acionando fallback seguro:', rpcError);
            }
        } catch (rpcErr) {
            console.warn('[AuditService] Erro ao invocar RPC gerar_protocolo_fiscal:', rpcErr);
        }

        // 2. Fallback resiliente no cliente com retry loop e resolução de colisão
        const now = new Date();
        const ano = now.getFullYear();
        const mes = String(now.getMonth() + 1).padStart(2, '0');
        const dia = String(now.getDate()).padStart(2, '0');
        const dataFormatada = `${mes}${dia}`;

        let baseSeq = await this.obterProximoSequencial(dataFormatada);

        for (let attempt = 0; attempt < 10; attempt++) {
            const currentSeq = baseSeq + attempt;
            const numeroProtocolo = attempt < 5
                ? `PROT-${ano}-${dataFormatada}-${String(currentSeq).padStart(5, '0')}`
                : `PROT-${ano}-${dataFormatada}-${String(currentSeq).padStart(5, '0')}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;

            const protocolo: Protocolos_Fiscais_DB = {
                numero_protocolo: numeroProtocolo,
                data_emissao: new Date().toISOString(),
                tipo_documento: tipoDocumento,
                entidade_id: entidadeId,
                entidade_tipo: entidadeTipo,
                municipio_id: munId,
                operador_id: operadorId,
                operador_nome: opNome,
                operador_login: opLogin,
                status: 'ATIVO',
                observacoes
            };

            try {
                const { error } = await supabase.from('protocolos_fiscais').insert(protocolo);
                if (!error) {
                    return numeroProtocolo;
                }

                // Se houver colisão de chave única (23505), prossegue no loop com o próximo sequencial
                if (error.code === '23505' || error.message?.includes('duplicate key') || error.message?.includes('unique constraint')) {
                    console.warn(`[AuditService] Colisão detectada para ${numeroProtocolo}. Tentando novo sequencial (tentativa ${attempt + 1})...`);
                    if (attempt === 4) {
                        baseSeq = Math.floor(Date.now() % 100000);
                    }
                    continue;
                }

                throw error;
            } catch (insertErr: any) {
                if (insertErr?.code === '23505' || insertErr?.message?.includes('duplicate key') || insertErr?.message?.includes('unique constraint')) {
                    continue;
                }
                console.error('[AuditService] Erro ao gerar protocolo no fallback:', insertErr);
                throw insertErr;
            }
        }

        // Fallback final com timestamp único em caso extremo
        const emergencyProtocol = `PROT-${ano}-${dataFormatada}-${Date.now().toString().slice(-5)}`;
        try {
            await supabase.from('protocolos_fiscais').insert({
                numero_protocolo: emergencyProtocol,
                data_emissao: new Date().toISOString(),
                tipo_documento: tipoDocumento,
                entidade_id: entidadeId,
                entidade_tipo: entidadeTipo,
                municipio_id: munId,
                operador_id: operadorId,
                operador_nome: opNome,
                operador_login: opLogin,
                status: 'ATIVO',
                observacoes
            });
            return emergencyProtocol;
        } catch {
            return emergencyProtocol;
        }
    }

    /**
     * Registra uma entrada no histórico de auditoria amigável.
    /**
     * Registra auditoria histórica no Supabase com suporte resiliente a assinaturas com 6 ou 7 argumentos.
     */
    async registrarAuditoria(
        protocoloReferencia: string,
        tipoAcao: TipoAcao,
        descricaoAcao: string,
        operadorId: number,
        arg5?: string | Record<string, any> | null,
        arg6?: Record<string, any> | null,
        arg7?: Record<string, any> | null
    ): Promise<void> {
        let opNome = this.context.currentOperator()?.name || 'Sistema';
        let dadosAnteriores: Record<string, any> | null = null;
        let dadosNovos: Record<string, any> | null = null;

        if (typeof arg5 === 'string') {
            opNome = arg5;
            dadosAnteriores = arg6 || null;
            dadosNovos = arg7 || null;
        } else if (arg7 !== undefined) {
            dadosAnteriores = (arg6 as any) || null;
            dadosNovos = (arg7 as any) || null;
        } else {
            dadosAnteriores = (arg5 as any) || null;
            dadosNovos = (arg6 as any) || null;
        }

        // Evitar erro de foreign key com sub-etapas de cirurgia (ex: PROT-2026-0228-00001.1)
        let finalProtocol = protocoloReferencia;
        let adendoDescricao = '';
        if (protocoloReferencia && protocoloReferencia.includes('.')) {
            finalProtocol = protocoloReferencia.split('.')[0];
            adendoDescricao = ` (Sub-protocolo: ${protocoloReferencia})`;
        }

        let detalhesAmigaveis = '';
        if (dadosAnteriores && dadosNovos && typeof dadosAnteriores === 'object' && typeof dadosNovos === 'object') {
            const ignoringFields = ['auditTrail', 'latestProtocol', 'id', 'patientId', 'municipalityId'];
            const changes: string[] = [];
            const allKeys = new Set([...Object.keys(dadosAnteriores), ...Object.keys(dadosNovos)]);

            const fieldMap: Record<string, string> = {
                status: 'Status',
                priority: 'Prioridade',
                prioridade: 'Prioridade',
                urgency_priority: 'Urgência',
                urgencia: 'Urgência',
                procedureId: 'Procedimento',
                procedimentoId: 'Procedimento',
                procedureName: 'Procedimento',
                procedimento: 'Procedimento',
                schedulingData: 'Agendamento',
                dataAgendamento: 'Data Agendamento',
                horario: 'Horário',
                outcomeData: 'Resultado',
                requestDate: 'Data Solicitação',
                dataSolicitacao: 'Data Solicitação',
                phone: 'Telefone',
                telefone: 'Telefone',
                name: 'Nome',
                nomePaciente: 'Nome do Paciente',
                socialName: 'Nome Social',
                patientName: 'Paciente',
                patientCpf: 'CPF do Paciente',
                cpf: 'CPF',
                location: 'Local',
                local: 'Local',
                executorName: 'Executor',
                enteExecutor: 'Ente Executor',
                addressZip: 'CEP',
                addressCity: 'Cidade',
                addressStreet: 'Rua',
                addressDistrict: 'Bairro',
                addressNumber: 'Número'
            };

            for (const key of allKeys) {
                if (ignoringFields.includes(key)) continue;
                const label = fieldMap[key];
                if (!label) continue;

                const oldVal = dadosAnteriores[key];
                const newVal = dadosNovos[key];

                if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
                    let oldDisplay = oldVal === null || oldVal === undefined ? 'Não definido' : oldVal;
                    let newDisplay = newVal === null || newVal === undefined ? 'Não definido' : newVal;

                    if (typeof oldVal === 'object' && oldVal !== null) oldDisplay = '(alterado)';
                    if (typeof newVal === 'object' && newVal !== null) newDisplay = '(alterado)';

                    if (key === 'urgency_priority' || key === 'urgencia') {
                        oldDisplay = oldVal ? 'Sim' : 'Não';
                        newDisplay = newVal ? 'Sim' : 'Não';
                    }

                    changes.push(`${label}: ${oldDisplay} -> ${newDisplay}`);
                }
            }

            if (changes.length > 0) {
                detalhesAmigaveis = ` | Mudanças: ${changes.join('; ')}`;
            }
        }

        const municipioId = this.context.currentMunicipalityId() || dadosNovos?.municipalityId || dadosNovos?.municipality_id || dadosAnteriores?.municipalityId || dadosAnteriores?.municipality_id || 'SISTEMA';

        // Helper safely stringify with error handling
        const safelyStringify = (data: any): string | null => {
            if (!data) return null;
            if (typeof data === 'string') return data;
            if (typeof data !== 'object') return String(data);
            try {
                return JSON.stringify(data);
            } catch (e) {
                console.warn('[AuditService] Não foi possível stringify dados:', e);
                return null;
            }
        };

        // Compactar dados antes de salvar (com fallback seguro)
        const dadosAnterioresCompactados = dadosAnteriores ? (CompressionUtil.compress(dadosAnteriores) || safelyStringify(dadosAnteriores)) : null;
        const dadosNovosCompactados = dadosNovos ? (CompressionUtil.compress(dadosNovos) || safelyStringify(dadosNovos)) : null;

        try {
            const { error } = await supabase.from('historico_auditoria').insert({
                protocolo_referencia: finalProtocol,
                data_hora: new Date().toISOString(),
                tipo_acao: tipoAcao,
                descricao_acao: descricaoAcao + adendoDescricao + detalhesAmigaveis,
                municipio_id: municipioId,
                operador_id: operadorId,
                operador_nome: opNome,
                dados_anteriores: dadosAnterioresCompactados,
                dados_novos: dadosNovosCompactados,
                formulario_origem: 'WEB_OPERATOR'
            });

            if (error) throw error;
        } catch (err) {
            console.error('[AuditService] Erro ao registrar auditoria:', err);
        }
    }

async loadSystemLogs(limit = 100, offset = 0, municipalityId?: string) {
        try {
            let query = supabase
                .from('historico_auditoria')
                .select('*', { count: 'exact' });

            // Filtrar por municipality se fornecido
            if (municipalityId && municipalityId !== 'SISTEMA') {
                query = query.eq('municipio_id', municipalityId);
            }

            const { data, error, count } = await query
                .order('data_hora', { ascending: false })
                .range(offset, offset + limit - 1);

            if (error) throw error;

            const mapped = (data || []).map(h => ({
                id: h.id,
                protocol: h.protocolo_referencia,
                timestamp: new Date(h.data_hora),
                action: h.tipo_acao,
                target: h.descricao_acao,
                user: h.operador_nome,
                municipalityId: h.municipio_id,
                details: {
                    tipoAcao: h.tipo_acao,
                    dadosAnteriores: this.parseJsonField(h.dados_anteriores),
                    dadosNovos: this.parseJsonField(h.dados_novos)
                }
            } as SystemLog));

            this.systemLogsTable.set(mapped);
            return {
                data: mapped,
                total: count || 0,
                offset,
                limit
            };
        } catch (err) {
            console.error('[AuditService] Error loading logs:', err);
            return { data: [] as SystemLog[], total: 0, offset, limit };
        }
    }

    async findFiscalHistoryAsync(referenceLog: SystemLog, municipalityId?: string | null): Promise<SystemLog[]> {
        try {
            const protocolsToSearch = [referenceLog.protocol];
            const parentProtocol = referenceLog.protocol.split('.')[0];
            if (parentProtocol !== referenceLog.protocol) {
                protocolsToSearch.push(parentProtocol);
            }

            let query = supabase
                .from('historico_auditoria')
                .select('*')
                .in('protocolo_referencia', protocolsToSearch)
                .order('data_hora', { ascending: false });

            const effectiveMun = municipalityId || referenceLog.municipalityId;
            if (effectiveMun && effectiveMun !== 'SISTEMA' && effectiveMun !== 'GLOBAL') {
                query = query.eq('municipio_id', effectiveMun);
            }

            const { data: history, error } = await query;

            if (error) throw error;

            if (history && history.length > 0) {
                const refDetails = referenceLog.details || {};
                const refPatient = refDetails.patientName || refDetails.name || (refDetails.appointment?.patient?.name) || 'Paciente';
                const refProcedure = refDetails.procedureName || refDetails.procedure || (refDetails.appointment?.procedure?.name) || 'Procedimento';
                const rawRefStatus = refDetails.status || (refDetails.appointment?.status);
                const refStatus = (rawRefStatus && rawRefStatus !== 'ATIVO') ? rawRefStatus : 'Aguardando';
                const refPriority = refDetails.priority || (refDetails.appointment?.priority) || 'Normal';

                const isValidName = (val: any): boolean => {
                    if (!val || typeof val !== 'string') return false;
                    const clean = val.trim();
                    if (clean === '-' || clean === '' || clean === 'null' || clean === 'undefined') return false;
                    const badWords = ['agendamento', 'etapa', 'ciclo', 'procedimento', 'dispensada', 'cancelado', 'realizado', 'apto', 'ficha', 'alteração', 'alteracao'];
                    const lower = clean.toLowerCase();
                    return !badWords.some(b => lower.includes(b));
                };

                return history.map(h => {
                    const dn = this.parseJsonField(h.dados_novos) || {};
                    const da = this.parseJsonField(h.dados_anteriores) || {};
                    const desc = h.descricao_acao || '';
                    const action = h.tipo_acao;

                    // Resolução Estrita e Segura do Paciente
                    const rawPatient = dn.nomePaciente || dn.patientName || dn.name || da.nomePaciente || da.patientName;
                    const pName = isValidName(rawPatient) ? rawPatient : refPatient;

                    // Resolução do Procedimento
                    const rawProc = dn.procedimento || dn.nomeProcedimento || dn.procedureName || dn.procedure || da.procedimento || da.procedureName;
                    const prName = isValidName(rawProc) ? rawProc : refProcedure;

                    // Resolução de Status
                    let status = dn.status || da.status;
                    if (!status || status === 'ATIVO') {
                        if (action === 'CANCELAMENTO' || action === 'CANCEL' || desc.includes('encerrado') || desc.includes('Cancelamento') || desc.includes('-> Cancelado')) {
                            status = 'Cancelado';
                        } else if (action === 'CONFIRMACAO_REALIZACAO' || action === 'REALIZADO' || desc.includes('-> Realizado')) {
                            status = 'Realizado';
                        } else if (action === 'CONFIRMACAO_NAO_REALIZACAO' || desc.includes('Não Realizado') || desc.includes('-> Não Realizado')) {
                            status = 'Não Realizado';
                        } else if (action === 'AGENDAMENTO' || desc.includes('-> Agendado')) {
                            status = 'Agendado';
                        } else if (desc.includes('dispensada')) {
                            status = 'Dispensado';
                        } else if (action === 'CRIACAO' || action === 'ATENDIMENTO_INICIAL') {
                            status = 'Aguardando';
                        } else {
                            status = refStatus;
                        }
                    }

                    const priority = dn.prioridade || dn.priority || da.prioridade || refPriority;
                    const location = dn.local || dn.location || da.local || refDetails.location || '-';

                    return {
                        id: h.id,
                        protocol: h.protocolo_referencia,
                        timestamp: new Date(h.data_hora),
                        action: h.tipo_acao,
                        target: referenceLog.target,
                        user: h.operador_nome || 'Sistema',
                        municipalityId: h.municipio_id,
                        details: {
                            tipoAcao: h.tipo_acao,
                            descricao: desc,
                            patientName: pName,
                            name: pName,
                            procedureName: prName,
                            procedure: prName,
                            status: status,
                            priority: priority,
                            location: location,
                            changes: dn,
                            dadosAnteriores: da,
                            dadosNovos: dn,
                            appointment: refDetails.appointment
                        }
                    } as SystemLog;
                });
            }
        } catch (err) {
            console.error('[AuditService] Erro ao buscar histórico fiscal:', err);
        }
        return [];
    }

    private mapDbToSystemLog(h: any): SystemLog {
        const rawDetails = this.parseJsonField(h.dados_novos) || {};
        return {
            id: h.id,
            protocol: h.protocolo_referencia,
            timestamp: new Date(h.data_hora),
            action: h.tipo_acao,
            target: h.descricao_acao,
            user: h.operador_nome,
            operatorId: h.operador_id,
            municipalityId: h.municipio_id,
            details: {
                tipoAcao: h.tipo_acao,
                descricao: h.descricao_acao,
                procedureName: rawDetails.procedureName || rawDetails.procedure || null,
                dadosAnteriores: this.parseJsonField(h.dados_anteriores),
                dadosNovos: this.parseJsonField(h.dados_novos)
            }
        } as SystemLog;
    }

    async getUserActivity(identifier: string, operatorId?: number, municipalityId?: string | null): Promise<SystemLog[]> {
        try {
            let query = supabase.from('historico_auditoria')
                .select('*')
                .order('data_hora', { ascending: false })
                .limit(50);

            const effectiveMun = municipalityId || this.context.currentMunicipalityId();
            if (effectiveMun && effectiveMun !== 'SISTEMA' && effectiveMun !== 'GLOBAL') {
                query = query.ilike('municipio_id', effectiveMun);
            }

            const conditions: string[] = [];
            if (operatorId) conditions.push(`operador_id.eq.${operatorId}`);
            if (identifier) conditions.push(`operador_nome.ilike.%${identifier}%`);

            if (conditions.length > 0) {
                query = query.or(conditions.join(','));
            }

            const { data, error } = await query;
            if (error) throw error;

            return (data || []).map(h => this.mapDbToSystemLog(h));
        } catch (err) {
            console.error('[AuditService] Error fetching user activity:', err);
            return [];
        }
    }

    /**
     * Busca um feed global de atividades para o monitoramento central.
     * Suporta paginação (lazy load) e filtros de município/operador.
     */
    async getGlobalActivityFeed(filters: { municipalityId?: string | null, operatorId?: string | null, limit?: number, offset?: number }): Promise<SystemLog[]> {
        const { municipalityId, operatorId, limit = 50, offset = 0 } = filters;
        try {
            let query = supabase.from('historico_auditoria')
                .select('*')
                .order('data_hora', { ascending: false })
                .range(offset, offset + limit - 1);

            if (municipalityId) {
                query = query.eq('municipio_id', municipalityId);
            }
            if (operatorId) {
                query = query.eq('operador_id', Math.floor(Number(operatorId)));
            }

            const { data, error } = await query;
            if (error) throw error;

            return (data || []).map(h => this.mapDbToSystemLog(h));
        } catch (err) {
            console.error('[AuditService] getGlobalActivityFeed error:', err);
            return [];
        }
    }

    /**
     * Subscreve para novas entradas na tabela de auditoria em tempo real.
     * ATUALMENTE DESABILITADO - tabela historico_auditoria não está na publicação do realtime.
     */
    subscribeToGlobalChanges(callback: (log: SystemLog) => void) {
        // Realtime desabilitado - a tabela historico_auditoria não está na publicação supabase_realtime
        // O monitoramento funciona via polling (fetch global activity feed)
        console.log('[AuditService] Realtime desabilitado para historico_auditoria - usando polling');
        return { 
            unsubscribe: () => {
                console.log('[AuditService] Realtime unsubscribe called (no-op)');
            } 
        };
    }


    async fetchAuditLogs(month: string, municipalityId: string | null): Promise<SystemLog[]> {
        const start = `${month}-01T00:00:00`;
        const [y, m] = month.split('-').map(Number);
        const lastDay = new Date(y, m, 0).getDate();
        const end = `${month}-${lastDay}T23:59:59`;

        try {
            let query = supabase.from('historico_auditoria')
                .select('*')
                .gte('data_hora', start)
                .lte('data_hora', end)
                .order('data_hora', { ascending: false });

            if (municipalityId && municipalityId !== 'SISTEMA') {
                query = query.eq('municipio_id', municipalityId);
            }

            const { data: auditData, error } = await query;
            if (error) throw error;

            const logs: SystemLog[] = (auditData || []).map(h => {
                const dn = this.parseJsonField(h.dados_novos) || {};
                const da = this.parseJsonField(h.dados_anteriores) || {};
                const rawDetails = { ...da, ...dn };
                const desc = h.descricao_acao || '';
                const action = h.tipo_acao || '';

                // Extração inteligente de Paciente (suporte a múltiplas chaves e regex)
                let patientName = rawDetails.patientName || rawDetails.nomePaciente || rawDetails.nome_paciente || 
                                  rawDetails.paciente || rawDetails.name || rawDetails.nome || 
                                  rawDetails.patient?.name || rawDetails.patient?.social_name || null;

                if (!patientName || patientName === '-' || patientName === 'null') {
                    const agendamentoMatch = desc.match(/(?:AGENDAMENTO|Agendamento)\s+(.+?)\s+(?:PARA|para)\s+([^|;]+)/i);
                    if (agendamentoMatch && agendamentoMatch[2]) {
                        patientName = agendamentoMatch[2].trim();
                    } else {
                        const demogMatch = desc.match(/(?:Alteração de dados demográficos|Paciente|Nome|Arquivamento de paciente \(histórico preservado\)\.\s*Nome):\s*([^|;]+)/i);
                        if (demogMatch && demogMatch[1]) {
                            patientName = demogMatch[1].trim();
                        }
                    }
                }

                // Extração inteligente de Procedimento (suporte a múltiplas chaves e regex)
                let procedureName = rawDetails.procedureName || rawDetails.nomeProcedimento || rawDetails.nome_procedimento || 
                                    rawDetails.procedimento || rawDetails.procedure || rawDetails.appointment?.procedureName || 
                                    rawDetails.appointment?.procedure?.name || null;

                let procedureId = rawDetails.procedureId || rawDetails.procedimentoId || rawDetails.procedimento_id || 
                                  rawDetails.id_procedimento || rawDetails.procedure_id || 
                                  rawDetails.appointment?.procedureId || rawDetails.appointment?.procedure_id || null;

                if (!procedureName || procedureName === '-' || procedureName === 'null') {
                    const agendamentoMatch = desc.match(/(?:AGENDAMENTO|Agendamento)\s+(.+?)\s+(?:PARA|para)/i);
                    if (agendamentoMatch && agendamentoMatch[1]) {
                        procedureName = agendamentoMatch[1].trim();
                    } else {
                        const etapaMatch = desc.match(/Iniciada Etapa \d+:\s*([^|;]+)/i);
                        if (etapaMatch && etapaMatch[1]) {
                            procedureName = etapaMatch[1].trim();
                        } else {
                            const cadMatch = desc.match(/Procedimento cadastrado no sistema:\s*([^\[|;]+)/i);
                            if (cadMatch && cadMatch[1]) {
                                procedureName = cadMatch[1].trim();
                            }
                        }
                    }
                }

                // Extração inteligente de Status com fallback contextual
                let status = rawDetails.status || rawDetails.novoStatus || rawDetails.appointment?.status || null;
                if (!status || status === '-' || status === 'null') {
                    if (desc.includes('dispensada')) status = 'Dispensado';
                    else if (desc.includes('reiniciado') || action === 'CIRURGIA_ETAPA' || action === 'SURGERY_CYCLE_STEP') status = 'Em Andamento';
                    else if (desc.includes('concluído') || desc.includes('finalizadas')) status = 'Apto para Cirurgia';
                    else if (action === 'CANCELAMENTO' || action === 'CANCEL' || desc.includes('encerrado')) status = 'Cancelado';
                    else if (action === 'CONFIRMACAO_REALIZACAO' || action === 'REALIZADO' || desc.includes('-> Realizado')) status = 'Realizado';
                    else if (action === 'CONFIRMACAO_NAO_REALIZACAO' || desc.includes('Não Realizado')) status = 'Não Realizado';
                    else if (action === 'AGENDAMENTO' || desc.includes('-> Agendado')) status = 'Agendado';
                    else if (action === 'CRIACAO' || action === 'ATENDIMENTO_INICIAL') status = 'Aguardando';
                    else if (action === 'EDICAO_DADOS') status = 'Atualizado';
                    else status = 'Aguardando';
                }

                const priority = rawDetails.priority || rawDetails.prioridade || rawDetails.appointment?.priority || 'Normal';
                const location = rawDetails.location || rawDetails.local || rawDetails.scheduleLocation || 
                                 rawDetails.schedulingData?.location || rawDetails.outcome?.location || 
                                 rawDetails.appointment?.schedule_location || null;

                const schedulingData = rawDetails.schedulingData || 
                    (rawDetails.dataAgendamento ? { date: rawDetails.dataAgendamento, time: rawDetails.horario } : null) || 
                    rawDetails.appointment?.schedulingData || null;

                const acsName = rawDetails.acsName || rawDetails.acs || rawDetails.patient?.acsName || null;
                const acsContact = rawDetails.acsContact || rawDetails.contatoAcs || rawDetails.patient?.acsContact || null;
                const observacoes = rawDetails.observacoes || rawDetails.observacao || rawDetails.notes || 
                                    rawDetails.justificativa || rawDetails.justificativaUrgencia || 
                                    rawDetails.outcome_reason || rawDetails.appointment?.outcome_reason || null;

                const parentProcedureName = rawDetails.parentProcedureName || rawDetails.surgeryName || 
                                           rawDetails.agendamentoPai || rawDetails.protocoloPai || null;

                return {
                    id: h.id,
                    protocol: h.protocolo_referencia,
                    timestamp: new Date(h.data_hora),
                    action: h.tipo_acao,
                    target: h.descricao_acao || '-',
                    user: h.operador_nome,
                    operatorId: h.operador_id,
                    municipalityId: h.municipio_id,
                    details: {
                        tipoAcao: h.tipo_acao,
                        descricao: h.descricao_acao,
                        procedureName: procedureName || null,
                        procedureId: procedureId ? Number(procedureId) : null,
                        status: status || null,
                        priority: priority,
                        location: location || null,
                        patientName: patientName || null,
                        cpf: rawDetails.cpf || rawDetails.patientCpf || rawDetails.patient?.cpf || null,
                        schedulingData: schedulingData,
                        acsName: acsName,
                        acsContact: acsContact,
                        observacoes: observacoes,
                        parentProcedureName: parentProcedureName,
                        changes: rawDetails,
                        dadosNovos: dn,
                        dadosAnteriores: da
                    }
                } as SystemLog;
            });

            // 2. Busca também os agendamentos registrados no banco para o mês de referência (incluindo cancelados e inativos)
            try {
                let apptsQuery = supabase.from('appointments')
                    .select(`
                        id, patient_id, procedure_id, status, priority, registration_date,
                        schedule_date, schedule_time, schedule_location, request_date,
                        outcome_date, outcome_reason, outcome_location, urgency_priority,
                        latest_protocol, pre_surgery_completed, anatomical_region,
                        executor_id, executor_name, is_active, created_at, audit_trail,
                        municipality_id,
                        patients (id, name, social_name, cpf, phone, sus_card, birth_date, address_street, address_number, address_district, address_city, address_uf, acs_id),
                        procedures (id, name, category),
                        acs (id, name)
                    `);

                if (municipalityId && municipalityId !== 'SISTEMA') {
                    apptsQuery = apptsQuery.eq('municipality_id', municipalityId);
                }

                const { data: dbAppts } = await apptsQuery;
                if (dbAppts && dbAppts.length > 0) {
                    const logsByProtocol = new Map<string, SystemLog>();
                    const logsByApptId = new Map<number, SystemLog>();

                    logs.forEach(l => {
                        if (l.protocol) logsByProtocol.set(l.protocol, l);
                        if (l.details?.procedureId) logsByApptId.set(Number(l.details.procedureId), l);
                    });

                    dbAppts.forEach(a => {
                        const schedDate = (a.schedule_date || (a as any).scheduling_data?.date || '').substring(0, 7);
                        const regDate = (a.registration_date ? String(a.registration_date).substring(0, 7) : '');
                        const outDate = (a.outcome_date || '').substring(0, 7);
                        const reqDate = (a.request_date || '').substring(0, 7);
                        const creatDate = a.created_at ? String(a.created_at).substring(0, 7) : '';

                        const isInMonth = schedDate === month || regDate === month || outDate === month || reqDate === month || creatDate === month;
                        if (!isInMonth) return;

                        const p = Array.isArray(a.patients) ? a.patients[0] : a.patients;
                        const proc = Array.isArray(a.procedures) ? a.procedures[0] : a.procedures;
                        const acsObj = Array.isArray(a.acs) ? a.acs[0] : a.acs;

                        const patientName = p?.name || p?.social_name || null;
                        const procedureName = proc?.name || null;
                        const sched = a.schedule_date ? { date: a.schedule_date, time: a.schedule_time || '', location: a.schedule_location } : null;

                        const existingLog = (a.latest_protocol ? logsByProtocol.get(a.latest_protocol) : null) || logsByApptId.get(a.id);
                        if (existingLog) {
                            if (existingLog.details) {
                                if (!existingLog.details.patientName && patientName) existingLog.details.patientName = patientName;
                                if (!existingLog.details.procedureName && procedureName) existingLog.details.procedureName = procedureName;
                                if (!existingLog.details.procedureId && a.procedure_id) existingLog.details.procedureId = a.procedure_id;
                                if (!existingLog.details.cpf && p?.cpf) existingLog.details.cpf = p.cpf;
                                if (a.status && (!existingLog.details.status || existingLog.details.status === 'Aguardando')) {
                                    existingLog.details.status = a.status;
                                }
                                if (sched && !existingLog.details.schedulingData) {
                                    existingLog.details.schedulingData = sched;
                                }
                                if (a.schedule_location && !existingLog.details.location) {
                                    existingLog.details.location = a.schedule_location;
                                }
                                if (a.outcome_reason && !existingLog.details.observacoes) {
                                    existingLog.details.observacoes = a.outcome_reason;
                                }
                            }
                        } else {
                            const actionType = a.status === 'Agendado' ? 'AGENDAMENTO' : 
                                              (a.status === 'Realizado' ? 'CONFIRMACAO_REALIZACAO' : 
                                              (a.status === 'Cancelado' ? 'CANCELAMENTO' : 
                                              (a.status === 'Não Realizado' ? 'CONFIRMACAO_NAO_REALIZACAO' :
                                              (a.status === 'Dispensado' ? 'CIRURGIA_ETAPA' : 'CRIACAO'))));

                            const itemDate = (a.status === 'Agendado' && a.schedule_date) ? `${a.schedule_date}T${a.schedule_time || '08:00:00'}` : 
                                            ((a.status === 'Realizado' || a.status === 'Cancelado') && a.outcome_date ? `${a.outcome_date}T08:00:00` : 
                                            (a.registration_date || a.created_at || `${month}-01T00:00:00`));

                            // Extrai operador histórico se disponível no audit_trail
                            let trailUser: string | null = null;
                            if (Array.isArray(a.audit_trail) && a.audit_trail.length > 0) {
                                const lastEv = a.audit_trail[a.audit_trail.length - 1];
                                if (lastEv?.user && lastEv.user !== 'Sistema' && !lastEv.user.startsWith('Operador #')) {
                                    trailUser = lastEv.user;
                                }
                            }

                            logs.push({
                                id: 900000000 + a.id,
                                protocol: a.latest_protocol || `AGD-${String(a.id).padStart(6, '0')}`,
                                timestamp: new Date(itemDate),
                                action: actionType,
                                target: `Agendamento ${procedureName || ''} para ${patientName || ''}`,
                                user: trailUser || null,
                                operatorId: null,
                                municipalityId: a.municipality_id,
                                details: {
                                    tipoAcao: actionType,
                                    descricao: `Atendimento #${a.id} (${a.status}) no sistema para ${procedureName || 'procedimento'}`,
                                    procedureName: procedureName,
                                    procedureId: a.procedure_id,
                                    status: a.status,
                                    priority: a.priority || 'Normal',
                                    location: a.schedule_location || a.outcome_location || a.executor_name || 'SEMUSGEL',
                                    patientName: patientName,
                                    cpf: p?.cpf || null,
                                    schedulingData: sched,
                                    acsName: acsObj?.name || null,
                                    acsContact: null,
                                    observacoes: a.outcome_reason || (a.status === 'Cancelado' ? 'Cancelamento de atendimento' : null),
                                    parentProcedureName: null,
                                    changes: {},
                                    dadosNovos: {
                                        id: a.id,
                                        appointmentId: a.id,
                                        status: a.status,
                                        procedimento: procedureName,
                                        nomePaciente: patientName,
                                        local: a.schedule_location || a.outcome_location || a.executor_name,
                                        dataAgendamento: a.schedule_date,
                                        horario: a.schedule_time,
                                        dataDesfecho: a.outcome_date,
                                        motivoCancelamento: a.outcome_reason
                                    },
                                    dadosAnteriores: {}
                                }
                            } as SystemLog);
                        }
                    });
                }
            } catch (apptErr) {
                console.warn('[AuditService] Erro ao carregar agendamentos complementares do mês:', apptErr);
            }

            // Ordena todos os logs do mês cronologicamente decrescente
            return logs.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        } catch (err) {
            console.error('[AuditService] Error fetching audit logs:', err);
            return [];
        }
    }

    async findRelatedLogs(referenceLog: SystemLog, municipalityId?: string | null): Promise<SystemLog[]> {
        try {
            // Buscar no histórico de auditoria por protocolo de referência
            const parentProtocol = referenceLog.protocol.split('.')[0];

            let query = supabase.from('historico_auditoria')
                .select('*')
                .order('data_hora', { ascending: false });

            const orConditions: string[] = [`protocolo_referencia.eq.${referenceLog.protocol}`, `protocolo_referencia.eq.${parentProtocol}`];
            query = query.or(orConditions.join(','));

            const effectiveMun = municipalityId || referenceLog.municipalityId;
            if (effectiveMun && effectiveMun !== 'SISTEMA' && effectiveMun !== 'GLOBAL') {
                query = query.ilike('municipio_id', effectiveMun);
            }

            const { data, error } = await query;
            if (error) throw error;

            return (data || []).map(h => ({
                id: h.id,
                protocol: h.protocolo_referencia,
                timestamp: new Date(h.data_hora),
                action: h.tipo_acao,
                target: h.descricao_acao,
                user: h.operador_nome,
                municipalityId: h.municipio_id,
                details: {
                    ... (this.parseJsonField(h.dados_novos) || {}),
                    tipoAcao: h.tipo_acao
                }
            } as SystemLog));
        } catch (err) {
            console.error('[AuditService] Erro ao buscar logs relacionados:', err);
            return [];
        }
    }
}

// Helper interface for table typing
interface Protocolos_Fiscais_DB {
    numero_protocolo: string;
    data_emissao: string;
    tipo_documento: string;
    entidade_id: number;
    entidade_tipo: string;
    municipio_id: string;
    operador_id: number;
    operador_nome: string;
    operador_login: string;
    status: string;
    observacoes?: string;
}
